// src/pages/invoicing/create-tax-standalone.js
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { Link, useNavigate } from "react-router-dom";
import API_BASE_URL from "../../config";
import { Modal, Button } from "react-bootstrap";

const CreateTaxStandaloneStep1 = () => {
  const [keyword, setKeyword] = useState("");
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const navigate = useNavigate();

  const handleSearch = async () => {
    const sanitized = keyword.trim().replace(/\s+/g, " ");
    setSearchPerformed(true);

    try {
      const res = await fetch(
        `${API_BASE_URL}/invoicing/search_leads_for_pi?search=${encodeURIComponent(sanitized)}`,
        { credentials: "include" },
      );
      const result = await res.json();
      if (result.status === "success") {
        setLeads(result.data);
      } else {
        setLeads([]);
      }
    } catch (e) {
      console.error(e);
      setLeads([]);
    }
  };

  const selectLead = async (leadId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/crm/get_lead_details/${leadId}`,
        { credentials: "include" },
      );
      const data = await res.json();

      if (data.success && data.data) {
        // 🔑 Make Search flow the *only* active selection
        localStorage.removeItem("selected_ti_lead"); // <-- clear Add-Lead key
        localStorage.setItem("selected_tax_lead", JSON.stringify(data.data)); // <-- set Search key
        navigate("/pages/invoicing/create-ti-items");
      } else {
        alert("Failed to fetch full lead details.");
      }
    } catch (err) {
      console.error("Error fetching lead details:", err);
      alert("Something went wrong while selecting the lead.");
    }
  };

  useEffect(() => {
    document.title = "Create Tax Invoice (Standalone) - Select Lead | Webyappy";
  }, []);

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid px-3 py-3 col-lg-10 mx-auto">
          {/* Breadcrumb */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold mb-0 p-0">
              <li className="breadcrumb-item">
                <Link to="/home" className="text-muted">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/pages/invoicing/Invoicing" className="text-muted">
                  Invoicing
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-dark font-weight-bold">
                  Create Tax Invoice (Standalone) - Select Lead
                </span>
              </li>
            </ul>
          </div>

          <div className="card card-custom shadow-sm p-3">
            {/* Header toolbar */}
            <div className="d-flex justify-content-between align-items-center flex-wrap p-3 border-bottom">
              <h5 className="font-weight-bold mb-3 mb-md-0">
                Select Existing Lead or Add New
              </h5>
              <Link
                to="/pages/invoicing/Invoicing"
                className="btn btn-light font-weight-bold"
              >
                <i className="fas fa-arrow-left fa-xs"></i> Back
              </Link>
            </div>

            {/* Search + Add Lead */}
            <div className="row mb-4 mt-3">
              <div className="col-md-9 mb-2">
                <input
                  type="text"
                  className="form-control form-control-lg shadow-sm"
                  placeholder="Search by name, company, email or mobile"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
              </div>
              <div className="col-md-3 mt-1">
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={handleSearch}
                >
                  <i className="fas fa-search mr-1"></i> Search
                </Button>
              </div>

              <div className="col-md-3 mb-2 mt-2">
                <Link
                  to="/pages/invoicing/ti-addleads"
                  className="btn btn-outline-success w-100"
                >
                  <i className="fas fa-plus mr-1"></i> Add New Lead
                </Link>
              </div>
            </div>

            {/* Results */}
            {leads.length > 0 && (
              <div className="row">
                {leads.map((lead) => (
                  <div
                    key={lead.id}
                    className="col-xl-6 col-lg-6 col-md-6 col-12 mb-4 d-flex"
                  >
                    <div className="card card-custom shadow-sm px-4 py-4 position-relative h-100 w-100">
                      <div
                        className="position-absolute"
                        style={{ top: "1rem", right: "1rem" }}
                      >
                        <a
                          href={`/pages/crm/ViewLeadD/${lead.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="far fa-eye text-danger icon-lg"></i>
                        </a>
                      </div>

                      {/* Header: Profile + Info */}
                      <div className="d-flex align-items-start mb-3">
                        {lead.profile_pic ? (
                          <img
                            src={`${API_BASE_URL.replace(
                              "/index.php",
                              "",
                            )}/${lead.profile_pic}`}
                            alt={lead.full_name}
                            className="rounded-circle mr-4"
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                              border: "2px solid #a18dfa",
                            }}
                          />
                        ) : (
                          <div className="symbol symbol-50 mr-4">
                            <span
                              className="symbol-label rounded-circle text-white font-weight-bold d-flex align-items-center justify-content-center"
                              style={{
                                width: "50px",
                                height: "50px",
                                backgroundColor: "#a18dfa",
                                fontSize: "18px",
                              }}
                            >
                              {lead.full_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}

                        <div
                          className="flex-grow-1"
                          style={{ marginLeft: "10px" }}
                        >
                          <h5
                            className="text-dark font-weight-bold mb-1"
                            style={{ fontSize: "15px" }}
                          >
                            {lead.full_name}
                          </h5>
                          <div
                            className="text-dark"
                            style={{ fontSize: "13px" }}
                          >
                            <div className="mb-1">
                              {lead.designation || "—"}
                            </div>
                            <div className="font-weight-semibold">
                              {lead.company || "—"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Contact */}
                      <div
                        className="mb-2 text-dark"
                        style={{ fontSize: "13px", cursor: "pointer" }}
                        onClick={() => {
                          setSelectedLead(lead);
                          setShowContactModal(true);
                        }}
                      >
                        <i
                          className="fas fa-phone-alt text-primary mr-2"
                          style={{ marginRight: "10px" }}
                        ></i>
                        +91 {lead.mobile}{" "}
                        {lead.alt_mobile && ` | +91 ${lead.alt_mobile}`}
                      </div>

                      {/* Email */}
                      <div
                        className="mb-3 text-dark"
                        style={{ fontSize: "13px", cursor: "pointer" }}
                        onClick={() => {
                          setSelectedLead(lead);
                          setShowEmailModal(true);
                        }}
                      >
                        <i
                          className="fas fa-envelope text-primary mr-2"
                          style={{ marginRight: "10px" }}
                        ></i>
                        {lead.email} {lead.alt_email && ` | ${lead.alt_email}`}
                      </div>

                      {/* Tags */}
                      <div className="d-flex align-items-center flex-wrap mb-3">
                        {lead.funnel_stage && (
                          <span
                            className="badge badge-info font-weight-bold mr-2 mb-2 px-3 py-2"
                            style={{ fontSize: "12px", marginRight: "10px" }}
                          >
                            <i className="fas fa-filter mr-1"></i>{" "}
                            {lead.funnel_stage}
                          </span>
                        )}
                        {lead.priority && (
                          <span
                            className="badge badge-danger font-weight-bold mb-2 px-3 py-2"
                            style={{ fontSize: "12px" }}
                          >
                            {lead.priority} Priority
                          </span>
                        )}
                      </div>

                      {/* Assigned/Created Info */}
                      <div
                        className="d-flex flex-wrap align-items-center text-dark mb-3"
                        style={{ fontSize: "13px" }}
                      >
                        <div className="mr-4 mb-2 d-flex align-items-center">
                          <i
                            className="fas fa-user text-primary"
                            style={{ marginRight: "10px" }}
                          ></i>
                          <strong>Assigned to:</strong>&nbsp;
                          <span className="font-weight-bold">
                            {lead.assigned_to_name || "-"}
                          </span>
                        </div>
                        <div className="mb-2 d-flex align-items-center">
                          <i
                            className="fas fa-user-plus text-primary mr-1"
                            style={{ marginRight: "10px" }}
                          ></i>
                          {lead.created_by_name || "-"}
                          {lead.created_on && (
                            <>
                              &nbsp;|&nbsp;
                              {new Date(lead.created_on).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                },
                              )}{" "}
                              {new Date(lead.created_on).toLocaleTimeString(
                                "en-IN",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Select Button */}
                      <div className="text-right">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => selectLead(lead.id)}
                        >
                          <i className="fas fa-check mr-1"></i> Select
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searchPerformed && leads.length === 0 && (
              <div className="text-center py-10 text-dark">
                <h6 className="font-weight-bold">No Leads Found</h6>
                <p>Try adjusting your search keyword or add a new lead.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      <Modal
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
        centered
      >
        <div className="modal-content rounded-xl shadow p-4">
          <div className="modal-header py-2 border-0">
            <h5 className="modal-title font-weight-bold text-dark">
              Contact Options
            </h5>
            <button
              type="button"
              className="close"
              onClick={() => setShowContactModal(false)}
            >
              <i className="ki ki-close"></i>
            </button>
          </div>
          <div className="modal-body text-center py-3">
            {selectedLead && (
              <>
                <div className="mb-4">
                  <div className="font-weight-bold text-dark mb-2">
                    +91 {selectedLead.mobile}
                  </div>
                  <a
                    href={`tel:+91${selectedLead.mobile}`}
                    className="btn btn-primary font-weight-bold mr-3 px-5"
                    style={{ marginRight: "10px" }}
                  >
                    <i className="fas fa-phone-alt mr-2"></i>Call
                  </a>
                  <a
                    href={`https://wa.me/91${selectedLead.mobile}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-success font-weight-bold px-5"
                  >
                    <i className="fab fa-whatsapp mr-2"></i>WhatsApp
                  </a>
                </div>
                {selectedLead.alt_mobile && (
                  <div>
                    <div className="font-weight-bold text-dark mb-2">
                      +91 {selectedLead.alt_mobile}
                    </div>
                    <a
                      href={`tel:+91${selectedLead.alt_mobile}`}
                      className="btn btn-primary font-weight-bold mr-3 px-5"
                    >
                      <i className="fas fa-phone-alt mr-2"></i>Call
                    </a>
                    <a
                      href={`https://wa.me/91${selectedLead.alt_mobile}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-success font-weight-bold px-5"
                    >
                      <i className="fab fa-whatsapp mr-2"></i>WhatsApp
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Email Modal */}
      <Modal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        centered
      >
        <div className="modal-content rounded-xl shadow p-4">
          <div className="modal-header py-2 border-0">
            <h5 className="modal-title font-weight-bold text-dark">
              Choose Email
            </h5>
            <button
              type="button"
              className="close"
              onClick={() => setShowEmailModal(false)}
            >
              <i className="ki ki-close"></i>
            </button>
          </div>
          <div className="modal-body text-center py-3">
            {selectedLead?.email && (
              <a
                href={`mailto:${selectedLead.email}`}
                className="btn btn-primary font-weight-bold mb-3 w-100"
              >
                <i className="fas fa-envelope mr-2"></i> {selectedLead.email}
              </a>
            )}
            {selectedLead?.alt_email && (
              <a
                href={`mailto:${selectedLead.alt_email}`}
                className="btn btn-primary font-weight-bold w-100"
              >
                <i className="fas fa-envelope mr-2"></i>{" "}
                {selectedLead.alt_email}
              </a>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CreateTaxStandaloneStep1;
